import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  HelpCircle,
  Lightbulb,
  CheckCircle,
  Target,
  Award,
  Brain,
  Eye,
  EyeOff,
  Trophy,
  RefreshCw,
  Download,
  Share2
} from 'lucide-react';
import { Exam, ExamQuestion, ExamHint, ExamSolution, ExamPracticeSession } from '../types/exam.types';
import { examApi } from '../services/examApi';
import { authApi } from '../services/api';
import { shareApi } from '../services/shareApi';
import type { ChatMode } from '../services/api';
import { analytics } from '../services/analytics';
import StudyMaterialRenderer from './StudyMaterialRenderer';
import { MixedText } from '../utils/rtl';
import { Card, CardHeader, CardTitle, CardContent } from './ui/Card';
import { LoadingSpinner } from './ui/LoadingSpinner';
import { PageContainer } from './layout/PageContainer';
import ChatWidget from './study-session/ChatWidget';
import ErrorMessage from './ui/ErrorMessage';
import SelectableContent from './ui/SelectableContent';
import { Button } from './ui/Button';

const ExamPracticeView: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'he';
  const { courseId, examId } = useParams<{ courseId: string; examId: string }>();
  const navigate = useNavigate();

  // State
  const [exam, setExam] = useState<Exam | null>(null);
  const [questions, setQuestions] = useState<ExamQuestion[]>([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [practiceSession, setPracticeSession] = useState<ExamPracticeSession | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [completedQuestionIds, setCompletedQuestionIds] = useState<number[]>([]);
  const [updatingQuestionCompletion, setUpdatingQuestionCompletion] = useState(false);

  // Question state
  const [hints, setHints] = useState<{[questionId: number]: ExamHint[]}>({});
  const [solutions, setSolutions] = useState<{[questionId: number]: ExamSolution}>({});
  const [showHints, setShowHints] = useState<{[questionId: number]: number}>({});
  const [showSolution, setShowSolution] = useState<{[questionId: number]: boolean}>({});

  // Navigation state
  const [navigationHistory, setNavigationHistory] = useState<number[]>([]);
  const [isNavigating, setIsNavigating] = useState(false);

  // Chat state
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [chatHistory, setChatHistory] = useState<Array<{type: 'user' | 'ai', message: string}>>([]);
  const [messageFeedback, setMessageFeedback] = useState<{[messageIndex: number]: 'like' | 'dislike' | null}>({});
  const [pendingMessage, setPendingMessage] = useState<string | null>(null);
  const [chatMode, setChatMode] = useState<ChatMode>('fast');
  const formatQuestionLabel = useCallback((question?: ExamQuestion | null, fallbackIndex?: number) => {
    if (!question) {
      return typeof fallbackIndex === 'number' ? `${fallbackIndex + 1}` : '';
    }
    const rawSubpart = question.question_subpart?.trim();
    const normalizedSubpart = rawSubpart?.startsWith('.') ? rawSubpart.slice(1) : rawSubpart;
    if (normalizedSubpart) {
      return `${question.question_number}.${normalizedSubpart}`;
    }
    return `${question.question_number ?? (fallbackIndex !== undefined ? fallbackIndex + 1 : '')}`;
  }, []);
  const buildShareUrl = useCallback((token?: string | null) => {
    if (!token) return null;
    return `${window.location.origin}/share/exam/${token}`;
  }, []);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [shareLoading, setShareLoading] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);
  const [shareError, setShareError] = useState<string | null>(null);

  // Loading states
  const [loadingHints, setLoadingHints] = useState<{[questionId: number]: boolean}>({});
  const [loadingSolution, setLoadingSolution] = useState<{[questionId: number]: boolean}>({});

  // Solution Appearance State
  const [solutionFontSize, setSolutionFontSize] = useState(16); // Base font size in pixels

  // PDF Export State
  const [isExportingPdf, setIsExportingPdf] = useState(false);
  const [pdfExportError, setPdfExportError] = useState<string | null>(null);
  const [isGeneratingAnotherExam, setIsGeneratingAnotherExam] = useState(false);
  const [generateAnotherError, setGenerateAnotherError] = useState<string | null>(null);

  // VIP Status
  const [isVip, setIsVip] = useState(() => {
    try {
      const rawUser = localStorage.getItem('user');
      if (!rawUser) return false;
      return Boolean(JSON.parse(rawUser)?.is_vip);
    } catch {
      return false;
    }
  });

  useEffect(() => {
    if (isVip) {
      setChatMode('smart');
    }
  }, [isVip]);


  useEffect(() => {
    if (courseId && examId) {
      loadExamData();
    }
  }, [courseId, examId]);

  useEffect(() => {
    if (practiceSession) {
      setCompletedQuestionIds(practiceSession.completed_question_ids || []);
    }
  }, [practiceSession]);

  useEffect(() => {
    setShareUrl(buildShareUrl(exam?.share_token));
  }, [exam?.share_token, buildShareUrl]);

  const loadExamData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setShareError(null);
      setGenerateAnotherError(null);
      setShareCopied(false);

      // Load exam, questions, practice session, and user data
      const [examData, questionsData, sessionData, userData] = await Promise.all([
        examApi.getExam(parseInt(examId!)),
        examApi.getExamQuestions(parseInt(examId!)),
        examApi.startPracticeSession(parseInt(examId!)),
        authApi.getCurrentUser()
      ]);

      let practiceSessionData = sessionData;

      if (!sessionData.completed_question_ids) {
        try {
          practiceSessionData = await examApi.getPracticeSession(sessionData.id);
        } catch (fetchError) {
          console.error('Failed to fetch existing practice session details:', fetchError);
        }
      }

      setExam(examData);
      setQuestions(questionsData);
      setPracticeSession(practiceSessionData);
      setCompletedQuestionIds(practiceSessionData.completed_question_ids || []);

      // Check if there's a saved question index in localStorage
      const savedQuestionIndex = localStorage.getItem(`exam_${examId}_current_question`);
      const initialQuestionIndex = savedQuestionIndex !== null
        ? Math.min(parseInt(savedQuestionIndex), questionsData.length - 1)
        : practiceSessionData.current_question_index;

      setCurrentQuestionIndex(initialQuestionIndex);
      setNavigationHistory([initialQuestionIndex]);

      // Save the initial position if it's different from what was saved
      if (initialQuestionIndex !== practiceSessionData.current_question_index && examId) {
        localStorage.setItem(`exam_${examId}_current_question`, initialQuestionIndex.toString());
      }

      setIsVip(userData.is_vip); // Set VIP status from user data

    } catch (err: any) {
      console.error('Error loading exam data:', err);
      setError(t('errors:something_went_wrong'));
    } finally {
      setLoading(false);
    }
  }, [courseId, examId, t]);

  const handleShareExam = async () => {
    if (!exam) return;
    setShareError(null);
    setShareLoading(true);
    try {
      let token = exam.share_token;
      if (!exam.is_shared) {
        const response = await shareApi.enableExamShare(exam.id);
        token = response.share_token || token;
        setExam(prev => prev ? { ...prev, is_shared: true, share_token: token } : prev);
        setShareUrl(response.share_url || buildShareUrl(token));
      } else if (!shareUrl && token) {
        setShareUrl(buildShareUrl(token));
      }

      const urlToCopy = shareUrl || buildShareUrl(token);
      if (urlToCopy) {
        await navigator.clipboard.writeText(urlToCopy);
        setShareCopied(true);
        setTimeout(() => setShareCopied(false), 1500);
      }
    } catch (err) {
      console.error('Error sharing exam:', err);
      setShareError(t('exams:share.error', 'Unable to generate a share link right now.'));
    } finally {
      setShareLoading(false);
    }
  };

  const handleDisableExamShare = async () => {
    if (!exam?.is_shared) return;
    setShareError(null);
    setShareLoading(true);
    try {
      await shareApi.disableExamShare(exam.id);
      setExam(prev => prev ? { ...prev, is_shared: false } : prev);
      setShareUrl(null);
    } catch (err) {
      console.error('Error disabling exam share:', err);
      setShareError(t('exams:share.disableError', 'Unable to disable sharing right now.'));
    } finally {
      setShareLoading(false);
    }
  };

  const loadHints = async (questionId: number, level: number = 1) => {
    if (hints[questionId] && showHints[questionId] >= level) {
      // Show next hint level
      setShowHints(prev => ({ ...prev, [questionId]: level }));
      return;
    }

    setLoadingHints(prev => ({ ...prev, [questionId]: true }));
    try {
      const hintsData = await examApi.getQuestionHints(questionId);
      setHints(prev => ({ ...prev, [questionId]: hintsData }));
      setShowHints(prev => ({ ...prev, [questionId]: level }));

      // Update practice session stats
      if (practiceSession) {
        setPracticeSession(prev => prev ? { ...prev, hints_used: prev.hints_used + 1 } : prev);
      }

      analytics.track('Question Hint Viewed', {
        question_id: questionId,
        hint_level: level,
        exam_id: parseInt(examId!)
      });
    } catch (err: any) {
      console.error('Error loading hints:', err);
      if (err.response?.status === 404) {
        setError(t('exams:errors.hints_not_found'));
      } else {
        setError(t('exams:errors.hints_load_failed'));
      }
    } finally {
      setLoadingHints(prev => ({ ...prev, [questionId]: false }));
    }
  };

  const loadSolution = async (questionId: number) => {
    if (solutions[questionId]) {
      setShowSolution(prev => ({ ...prev, [questionId]: !prev[questionId] }));
      return;
    }

    setLoadingSolution(prev => ({ ...prev, [questionId]: true }));
    try {
      const solutionData = await examApi.getQuestionSolution(questionId);
      setSolutions(prev => ({ ...prev, [questionId]: solutionData }));
      setShowSolution(prev => ({ ...prev, [questionId]: true }));

      // Update practice session stats
      if (practiceSession) {
        setPracticeSession(prev => prev ? { ...prev, solutions_viewed: prev.solutions_viewed + 1 } : prev);
      }

      analytics.track('Question Solution Viewed', {
        question_id: questionId,
        exam_id: parseInt(examId!)
      });
    } catch (err: any) {
      console.error('Error loading solution:', err);
      if (err.response?.status === 404) {
        setError(t('exams:errors.solution_not_found'));
      } else {
        setError(t('exams:errors.solution_load_failed'));
      }
    } finally {
      setLoadingSolution(prev => ({ ...prev, [questionId]: false }));
    }
  };

  const navigateToQuestion = async (newIndex: number) => {
    if (!questions || newIndex < 0 || newIndex >= questions.length || isNavigating) return;

    setIsNavigating(true);
    try {
      // Update navigation history
      setNavigationHistory(prev => [...prev, newIndex]);

      // For bidirectional navigation, we don't need to call the API
      // We'll just update the local state
      setCurrentQuestionIndex(newIndex);

      // Save current question index to localStorage
      if (examId) {
        localStorage.setItem(`exam_${examId}_current_question`, newIndex.toString());
      }

      // Only update the practice session if we're moving forward
      if (practiceSession && newIndex > currentQuestionIndex) {
        try {
          await examApi.nextQuestion(practiceSession.id);
          setPracticeSession(prev => prev ? { ...prev, current_question_index: newIndex } : prev);
        } catch (err) {
          console.warn('Failed to update practice session on server:', err);
          // Don't block navigation if server update fails
        }
      }

      analytics.track('Question Navigation', {
        exam_id: parseInt(examId!),
        from_question: currentQuestionIndex + 1,
        to_question: newIndex + 1,
        direction: newIndex > currentQuestionIndex ? 'forward' : 'backward'
      });
    } catch (err) {
      console.error('Error navigating to question:', err);
      setError(t('exams:errors.navigation_failed'));
    } finally {
      setIsNavigating(false);
    }
  };

  const completeSession = async () => {
    if (!practiceSession) return;

    try {
      await examApi.completeSession(practiceSession.id);

      // Clear saved question index from localStorage when exam is completed
      if (examId) {
        localStorage.removeItem(`exam_${examId}_current_question`);
      }

      analytics.track('Exam Practice Completed', {
        course_id: parseInt(courseId!),
        exam_id: parseInt(examId!),
        total_questions: questions.length,
        hints_used: practiceSession.hints_used,
        solutions_viewed: practiceSession.solutions_viewed
      });

      // Redirect to exam list with success message
      navigate(`/course/${courseId}/exams`, {
        state: { message: t('exams:practice.completed') }
      });
    } catch (err: any) {
      console.error('Error completing practice session:', err);
      setError(t('errors:something_went_wrong'));
    }
  };

  const handleExportPdf = () => {
    if (!exam || !isVip) {
      setPdfExportError(t('exams:pdfExport.errors.vipRequired'));
      return;
    }

    // Navigate to PDF export view
    navigate(`/course/${courseId}/exam/${examId}/pdf-export`);
  };

  const handleGenerateAnotherExam = async () => {
    if (!exam || exam.exam_type !== 'ai_generated' || isGeneratingAnotherExam) {
      return;
    }

    try {
      setGenerateAnotherError(null);
      setIsGeneratingAnotherExam(true);
      const generatedExam = await examApi.generateAnotherAIExam(exam.id);
      analytics.track('AI Exam Variant Requested From Practice', {
        course_id: exam.course_id,
        source_exam_id: exam.id,
        generated_exam_id: generatedExam.id,
      });
      navigate(`/course/${exam.course_id}/exams/${generatedExam.id}/practice`);
    } catch (err: any) {
      console.error('Error generating another AI exam:', err);
      setGenerateAnotherError(
        err?.response?.data?.detail || t('exams:practice.createAnotherError')
      );
    } finally {
      setIsGeneratingAnotherExam(false);
    }
  };



  // Define current question and related state before using in callbacks
  const currentQuestion = questions && questions[currentQuestionIndex];
  const currentQuestionHints = currentQuestion ? hints[currentQuestion.id] || [] : [];
  const currentQuestionSolution = currentQuestion ? solutions[currentQuestion.id] : null;
  const showingHints = currentQuestion ? showHints[currentQuestion.id] || 0 : 0;
  const showingSolution = currentQuestion ? showSolution[currentQuestion.id] || false : false;
  const isCurrentQuestionDone = currentQuestion ? completedQuestionIds.includes(currentQuestion.id) : false;

  const canGoBack = currentQuestionIndex > 0;
  const canGoForward = questions && currentQuestionIndex < questions.length - 1;
  const isLastQuestion = questions && currentQuestionIndex === questions.length - 1;

  // Chat handlers
  const handleChatUpdate = useCallback((message: {type: 'user' | 'ai', message: string}) => {
    setChatHistory(prev => [...prev, message]);
  }, []);

  const handleMessageFeedback = useCallback((messageIndex: number, feedback: 'like' | 'dislike') => {
    setMessageFeedback(prev => ({
      ...prev,
      [messageIndex]: prev[messageIndex] === feedback ? null : feedback
    }));
  }, []);

  // Custom chat handler for exam-specific chat
  const customChatHandler = useCallback(async (message: string, mode: ChatMode) => {
    if (!exam || !practiceSession) {
      throw new Error('Exam or practice session not available');
    }

    try {
      const effectiveMode: ChatMode = isVip ? 'smart' : mode;
      const response = await examApi.chatWithExam(
        exam.id,
        message,
        {
          current_question_index: currentQuestionIndex,
          current_question: currentQuestion,
          hints_used: Object.keys(showHints).length,
          solutions_viewed: Object.keys(showSolution).filter(id => showSolution[parseInt(id)]).length
        },
        practiceSession.id,
        effectiveMode
      );

      return { response: response.response };
    } catch (error) {
      console.error('Exam chat error:', error);
      throw error;
    }
  }, [exam, practiceSession, currentQuestionIndex, currentQuestion, showHints, showSolution, isVip]);

  // Load chat history function
  const loadChatHistory = useCallback(async (modeOverride?: ChatMode) => {
    if (!exam) return;

    try {
      const effectiveModeOverride: ChatMode | undefined = isVip ? 'smart' : modeOverride;
      const historyResponse = await examApi.getExamChatHistory(exam.id, effectiveModeOverride);
      if (historyResponse.history && historyResponse.history.length > 0) {
        setChatHistory(historyResponse.history);
      } else {
        setChatHistory([]);
      }
      if (historyResponse.mode) {
        setChatMode(isVip ? 'smart' : (historyResponse.mode as ChatMode));
      } else if (effectiveModeOverride) {
        setChatMode(effectiveModeOverride);
      }
    } catch (error) {
      console.error('Failed to load chat history:', error);
      throw error;
    }
  }, [exam, isVip]);

  // Modified chat opening handler
  const handleChatToggle = useCallback(async () => {
    if (isChatOpen) {
      setIsChatOpen(false);
      setMessageFeedback({});
      setPendingMessage(null);
      return;
    }

    try {
      await loadChatHistory(chatMode);
    } catch (error) {
      console.error('Failed to load exam chat history on open:', error);
    }
    setIsChatOpen(true);
  }, [isChatOpen, loadChatHistory, chatMode]);

  const handleChatModeChange = useCallback(async (newMode: ChatMode) => {
    const previousMode = chatMode;
    setChatMode(newMode);
    try {
      await loadChatHistory(newMode);
    } catch (error) {
      setChatMode(previousMode);
      throw error;
    }
  }, [chatMode, loadChatHistory]);

  const handleToggleQuestionCompletion = useCallback(async () => {
    if (!practiceSession || !currentQuestion) {
      return;
    }

    const questionId = currentQuestion.id;
    const isCurrentlyDone = completedQuestionIds.includes(questionId);

    try {
      setUpdatingQuestionCompletion(true);
      const updatedSession = await examApi.setQuestionCompletion(
        practiceSession.id,
        questionId,
        !isCurrentlyDone
      );

      setPracticeSession(updatedSession);
      setCompletedQuestionIds(updatedSession.completed_question_ids || []);
    } catch (err) {
      console.error('Failed to update question completion status:', err);
    } finally {
      setUpdatingQuestionCompletion(false);
    }
  }, [practiceSession, currentQuestion, completedQuestionIds]);

  // Text Selection Handler
  const handleTextSelected = useCallback((selectedText: string, question: string) => {
    if (currentQuestion) {
      // Format the message with context
      const contextMessage = i18n.language === 'he' ? `לגבי החלק בפתרון: "${selectedText}"\n\nשאלה: ${question}` : `About this text from the solution: "${selectedText}"\n\nQuestion: ${question}`;
      
      // Set pending message and open chat - same pattern as QuestionsStep
      setPendingMessage(contextMessage);
      if (!isChatOpen) {
        handleChatToggle();
      }

      // Track analytics
      analytics.track('Text Selection Question Asked', {
        exam_id: parseInt(examId!),
        question_id: currentQuestion.id,
        selected_text_length: selectedText.length,
        question_length: question.length
      });
    }
  }, [currentQuestion, isChatOpen, handleChatToggle, examId]);

  if (loading) {
    return (
      <PageContainer maxWidth="2xl" spacing="normal" backgroundIntensity="none" showParticles={false}>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Card className="p-8 text-center max-w-md mx-auto constellation-orion">
            <LoadingSpinner size="lg" className="mx-auto mb-4" />
            <CardTitle className="text-surface-primary mb-2 font-hebrew">
              <MixedText>{`${t('exams:practice.loading.title')}`}</MixedText>
            </CardTitle>
            <p className="text-surface-secondary font-hebrew">
              <MixedText>{`${t('exams:practice.loading.description')}`}</MixedText>
            </p>
          </Card>
        </div>
      </PageContainer>
    );
  }

  if (error || !exam || !questions || questions.length === 0) {
    return (
      <PageContainer maxWidth="2xl" spacing="normal" backgroundIntensity="none" showParticles={false}>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="space-y-4">
            <ErrorMessage />
            <div className="text-center">
              <button
                onClick={() => navigate(`/course/${courseId}/exams`)}
                className="font-hebrew text-surface-secondary hover:text-vibrant-orange transition-colors"
              >
                <MixedText>{`${t('common:actions.back_to_exams')}`}</MixedText>
              </button>
            </div>
          </div>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer maxWidth="2xl" spacing="normal" backgroundIntensity="none" showParticles={false}>


      {/* Header */}
      <div className="mb-8">
        {/* Breadcrumb Navigation */}
        <div className="mb-6">
          <button
            onClick={() => navigate(`/course/${courseId}/exams`)}
            className={`flex items-center text-surface-secondary hover:text-vibrant-orange transition-colors group font-hebrew ${isRTL ? 'space-x-reverse space-x-2' : 'space-x-2'}`}
          >
            {isRTL ? (
              <ArrowLeft className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200 ml-3 mr-3" />
            ) : (
              <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform duration-200" />
            )}
            <span className={`font-medium ${isRTL ? 'text-right' : ''}`}>
              <MixedText className="text-bg-secondary hover:text-vibrant-orange transition-colors">{`${t('common:actions.back_to_exams')}`}</MixedText>
            </span>
          </button>
        </div>

        {/* Exam Header */}
        <Card className="p-6 constellation-orion bg-gradient-to-br from-muted-purple/20 to-vibrant-orange/10">
          <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
            <div className={`flex items-center ${isRTL ? 'flex-row-reverse space-x-reverse space-x-4' : 'space-x-4'}`}>
              <div className="w-16 h-16 bg-gradient-to-br from-muted-purple to-vibrant-orange rounded-2xl flex items-center justify-center">
                <Target className="w-8 h-8 text-white" />
              </div>
              <div className={`${isRTL ? 'text-right' : ''}`}>
                <h1 className={`text-2xl font-bold text-surface-primary mb-1 font-hebrew ${isRTL ? 'text-right' : ''}`}>
                  <MixedText>{`${exam.title}`}</MixedText>
                </h1>
                <p className={`text-surface-secondary font-hebrew ${isRTL ? 'text-right' : ''}`}>
                  <MixedText>{`${t('exams:practice.practiceMode')}`}</MixedText>
                </p>
              </div>
            </div>

            {/* Progress & Share */}
            <div className={`flex items-center gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <div className={`text-center ${isRTL ? 'text-right' : ''}`}>
                <div className={`text-2xl font-bold text-vibrant-orange font-hebrew ${isRTL ? 'text-right' : ''}`}>
                  {currentQuestionIndex + 1} / {questions?.length || 0}
                </div>
                <div className={`text-sm text-surface-secondary font-hebrew ${isRTL ? 'text-right' : ''}`}>
                  <MixedText>{`${t('exams:practice.questions')}`}</MixedText>
                </div>
              </div>
              <div className={`flex items-center ${isRTL ? 'flex-row-reverse gap-2' : 'gap-2'}`}>
                {exam.exam_type === 'ai_generated' && (
                  <Button
                    variant="secondary"
                    size="sm"
                    icon={<RefreshCw className="w-4 h-4" />}
                    iconPosition={isRTL ? 'right' : 'left'}
                    onClick={handleGenerateAnotherExam}
                    loading={isGeneratingAnotherExam}
                  >
                    <MixedText>
                      {isGeneratingAnotherExam
                        ? t('exams:practice.creatingAnother')
                        : t('exams:practice.createAnother')}
                    </MixedText>
                  </Button>
                )}
                <Button
                  variant={exam.is_shared ? 'secondary' : 'primary'}
                  size="sm"
                  icon={<Share2 className="w-4 h-4" />}
                  iconPosition={isRTL ? 'right' : 'left'}
                  onClick={handleShareExam}
                  loading={shareLoading}
                >
                  <MixedText>
                    {shareCopied
                      ? t('exams:share.copied', 'Link copied')
                      : exam.is_shared
                        ? t('exams:share.copy', 'Copy share link')
                        : t('exams:share.enable', 'Share read-only link')}
                  </MixedText>
                </Button>
                {exam.is_shared && (
                  <button
                    onClick={handleDisableExamShare}
                    disabled={shareLoading}
                    className="text-xs text-surface-secondary hover:text-red-500 underline underline-offset-4 transition-colors"
                  >
                    {t('exams:share.disable', 'Disable')}
                  </button>
                )}
              </div>
            </div>


          </div>

          {/* Progress Bar */}
          <div className="mt-4">
            <div className="w-full bg-surface-subtle rounded-full h-2">
              <div 
                className="bg-gradient-to-r from-muted-purple to-vibrant-orange h-2 rounded-full transition-all duration-500 ease-out"
                style={{ width: `${questions?.length ? ((currentQuestionIndex + 1) / questions.length) * 100 : 0}%` }}
              ></div>
            </div>
          </div>
          {shareError && (
            <div className={`mt-3 text-sm text-red-500 ${isRTL ? 'text-right' : ''}`}>
              <MixedText>{shareError}</MixedText>
            </div>
          )}
          {generateAnotherError && (
            <div className={`mt-3 text-sm text-red-500 ${isRTL ? 'text-right' : ''}`}>
              <MixedText>{generateAnotherError}</MixedText>
            </div>
          )}
        </Card>
      </div>

      {/* Navigation Bar - Moved to Top with Original Sidebar Styling */}
      <div className="mb-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Question Completion Button */}
          <button
            onClick={handleToggleQuestionCompletion}
            disabled={!currentQuestion || updatingQuestionCompletion}
            className={`p-4 rounded-2xl transition-all duration-200 hover:scale-105 font-hebrew flex items-center justify-center ${
              isRTL ? 'flex-row-reverse' : ''
            } bg-gradient-to-br from-muted-purple/85 to-muted-purple/95 
              hover:from-muted-purple/95 hover:to-muted-purple
              backdrop-blur-sm border border-muted-purple/30 
              shadow-lg hover:shadow-xl hover:shadow-muted-purple/30
              text-astronaut-white hover:text-white disabled:opacity-60 disabled:hover:scale-100 disabled:cursor-not-allowed`}
          >
            <div className={`flex items-center ${isRTL ? 'flex-row-reverse gap-4' : 'gap-4'}`}>
              <img
                src={isCurrentQuestionDone ? '/astronaut_winning.png' : '/astronaut_cool.png'}
                alt={isCurrentQuestionDone ? 'Question completed' : 'Mark question as done'}
                className="w-20 h-20 drop-shadow-[0_6px_12px_rgba(134,116,245,0.35)]"
              />
              <span className="text-2xl font-semibold flex items-center gap-3">
                {updatingQuestionCompletion && <RefreshCw className="w-5 h-5 animate-spin" />}
                <MixedText>
                  {isCurrentQuestionDone
                    ? t('exams:practice.questionDone', 'Question Done')
                    : t('exams:practice.markQuestionDone', 'Make Question Done')}
                </MixedText>
              </span>
            </div>
          </button>

          {/* Question Overview Card */}
          <Card className="p-3 constellation-ursa">
            <CardTitle className={`text-surface-primary mb-2 font-hebrew text-sm ${isRTL ? 'text-right' : ''}`}>
              <MixedText>{`📋 ${t('exams:practice.overview')}`}</MixedText>
            </CardTitle>
            
            <div className="flex flex-wrap gap-1">
              {questions && questions.map((question, index) => (
                <button
                  key={question.id}
                  onClick={() => navigateToQuestion(index)}
                  disabled={isNavigating}
                  className={`relative w-8 h-8 rounded-lg border-2 flex items-center justify-center text-xs font-medium transition-all duration-200 hover:scale-105 ${
                    index === currentQuestionIndex
                      ? 'border-vibrant-orange bg-vibrant-orange/20 text-vibrant-orange shadow-md'
                      : completedQuestionIds.includes(question.id)
                      ? 'border-emerald-500 bg-emerald-50 text-emerald-600 hover:bg-emerald-100'
                      : showHints[question.id] || showSolution[question.id]
                      ? 'border-muted-purple/50 bg-muted-purple/10 text-muted-purple hover:bg-muted-purple/20'
                      : 'border-surface-border bg-surface-subtle text-surface-secondary hover:border-surface-primary hover:bg-surface-hover'
                  } ${isNavigating ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                >
                  {formatQuestionLabel(question, index)}
                  {completedQuestionIds.includes(question.id) && (
                    <CheckCircle className="w-3 h-3 text-emerald-500 absolute -top-1 -right-1 bg-white rounded-full" />
                  )}
                </button>
              ))}
            </div>
          </Card>

          {/* Navigation Card */}
          <Card className="p-3 constellation-cassiopeia">
            <CardTitle className={`text-surface-primary mb-2 font-hebrew text-sm ${isRTL ? 'text-right' : ''}`}>
              <MixedText>{`🧭 ${t('exams:practice.navigation')}`}</MixedText>
            </CardTitle>
            
            <div className="space-y-2">
              <button
                onClick={() => navigateToQuestion(currentQuestionIndex - 1)}
                disabled={!canGoBack || isNavigating}
                className={`w-full btn-secondary flex items-center justify-center transition-all duration-200 font-hebrew py-2 text-sm ${
                  isNavigating ? 'opacity-50 cursor-not-allowed' : ''
                } ${isRTL ? 'flex-row-reverse' : ''}`}
              >
                <ChevronLeft className={`w-4 h-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                <span className="text-sm">
                  <MixedText>{`${t('exams:practice.previousQuestion')}`}</MixedText>
                </span>
              </button>

              <button
                onClick={() => navigateToQuestion(currentQuestionIndex + 1)}
                disabled={!canGoForward || isNavigating}
                className={`w-full btn-secondary flex items-center justify-center transition-all duration-200 font-hebrew py-2 text-sm ${
                  isNavigating ? 'opacity-50 cursor-not-allowed' : ''
                } ${isRTL ? 'flex-row-reverse' : ''}`}
              >
                <span className="text-sm">
                  <MixedText>{`${t('exams:practice.nextQuestion')}`}</MixedText>
                </span>
                <ChevronRight className={`w-4 h-4 ${isRTL ? 'mr-2' : 'ml-2'}`} />
              </button>
            </div>
          </Card>
        </div>
      </div>

      {/* Main Content - Now Full Width */}
      <div className="space-y-6">
        {/* Question Card */}
        <Card className="constellation-cassiopeia">
          <CardHeader>
            <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
              <CardTitle className={`text-surface-primary font-hebrew ${isRTL ? 'text-right' : ''}`}>
                <MixedText>{`${t('exams:practice.question')} ${formatQuestionLabel(currentQuestion, currentQuestionIndex)}`}</MixedText>
              </CardTitle>
              <div className={`flex items-center space-x-4 ${isRTL ? 'space-x-reverse' : ''}`}>
                {currentQuestion?.points && (
                  <div className={`flex items-center ${isRTL ? 'space-x-reverse space-x-2' : 'space-x-2'}`}>
                    <Award className="w-4 h-4 mr-2 ml-2 text-vibrant-orange" />
                    <span className={`text-sm font-medium text-vibrant-orange font-hebrew ${isRTL ? 'text-right' : ''}`}>
                      <MixedText>{`${currentQuestion.points} ${t('common:labels.points')}`}</MixedText>
                    </span>
                  </div>
                )}
                {currentQuestion?.difficulty_level && (
                  <div className={`flex items-center px-2 py-1 rounded-full text-xs font-medium bg-surface-subtle text-surface-primary ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <span><MixedText>{`${currentQuestion.difficulty_level}`}</MixedText></span>
                    <Brain className={`w-3 h-3 ${isRTL ? 'ml-2 mr-2' : 'mr-2 ml-2'}`} />
                  </div>
                )}
              </div>
            </div>
          </CardHeader>

          <CardContent>
            <div className={`prose prose-lg max-w-none ${isRTL ? 'text-right' : ''}`}>
              {currentQuestion ? (
                <StudyMaterialRenderer
                  content={currentQuestion.question_text}
                  materialType="question"
                  isRTL={isRTL}
                />
              ) : (
                <p className="text-surface-secondary font-hebrew">
                  <MixedText>{`⏳ ${t('exams:practice.loadingQuestion')}`}</MixedText>
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Hints and Solution - Full Width */}
        <div className="space-y-6">
          {/* Hints Section */}
          <Card className="constellation-ursa">
            <CardHeader>
              <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
                <CardTitle className={`text-surface-primary font-hebrew ${isRTL ? 'text-right' : ''}`}>
                  <MixedText>{`💡 ${t('exams:practice.hints.title')}`}</MixedText>
                </CardTitle>
                <div className={`flex items-center gap-2 ${isRTL ? 'space-x-reverse' : ''}`}>
                  {currentQuestion && (
                    <button
                      onClick={() => loadHints(currentQuestion.id, 1)}
                      disabled={loadingHints[currentQuestion.id]}
                      className={`btn-secondary text-sm flex items-center transition-all duration-200 font-hebrew ${
                        showingHints >= 1 ? 'bg-muted-purple/20 border-muted-purple/30 text-muted-purple' : ''
                      }`}
                    >
                      <Lightbulb className="w-4 h-4 mr-2" />
                      <MixedText>
                        {showingHints >= 1 
                          ? `${t('exams:practice.hints.firstShown')}`
                          : `${t('exams:practice.hints.getFirst')}`
                        }
                      </MixedText>
                    </button>
                  )}
                  {currentQuestion && currentQuestionHints.length > 1 && showingHints >= 1 && (
                    <button
                      onClick={() => loadHints(currentQuestion.id, 2)}
                      disabled={loadingHints[currentQuestion.id]}
                      className={`btn-secondary text-sm flex items-center transition-all duration-200 font-hebrew ${
                        showingHints >= 2 ? 'bg-muted-purple/20 border-muted-purple/30 text-muted-purple' : ''
                      }`}
                    >
                      <Lightbulb className="w-4 h-4 mr-2" />
                      <MixedText>
                        {showingHints >= 2 
                          ? `${t('exams:practice.hints.secondShown')}`
                          : `${t('exams:practice.hints.getSecond')}`
                        }
                      </MixedText>
                    </button>
                  )}
                </div>
              </div>
            </CardHeader>

            <CardContent>
              {showingHints === 0 ? (
                <div className="text-center py-8">
                  <HelpCircle className="w-12 h-12 text-surface-muted mx-auto mb-4 transition-all duration-300 hover:text-vibrant-orange" />
                </div>
              ) : (
                <div className="space-y-4">
                  {currentQuestionHints.slice(0, showingHints).map((hint, index) => (
                    <div 
                      key={hint.id} 
                      className="p-4 bg-muted-purple/10 rounded-lg border border-muted-purple/20 transition-all duration-300 hover:bg-muted-purple/15"
                      style={{ animationDelay: `${index * 150}ms` }}
                    >
                      <div className={`flex items-start ${isRTL ? 'space-x-reverse space-x-3' : 'space-x-3'}`}>
                        <div className="w-6 h-6 bg-muted-purple rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-white text-sm font-bold">{index + 1}</span>
                        </div>
                        <div className="flex-1">
                          <StudyMaterialRenderer
                            content={hint.hint_text}
                            materialType="hint"
                            isRTL={isRTL}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Solution Section */}
          <Card className="constellation-orion">
            <CardHeader>
              <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
                <CardTitle className={`text-surface-primary font-hebrew ${isRTL ? 'text-right' : ''}`}>
                  <MixedText>{`✅ ${t('exams:practice.solution.title')}`}</MixedText>
                </CardTitle>
                {currentQuestion && (
                  <button
                    onClick={() => loadSolution(currentQuestion.id)}
                    disabled={loadingSolution[currentQuestion.id]}
                    className={`btn-primary text-sm flex items-center transition-all duration-200 font-hebrew ${
                      showingSolution ? 'bg-vibrant-orange/90 hover:bg-vibrant-orange' : ''
                    }`}
                  >
                    {loadingSolution[currentQuestion.id] ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        <MixedText>{`${t('common:labels.loading')}`}</MixedText>
                      </>
                    ) : showingSolution ? (
                      <>
                        <EyeOff className="w-4 h-4 mr-2" />
                        <MixedText>{`${t('exams:practice.solution.hide')}`}</MixedText>
                      </>
                    ) : (
                      <>
                        <Eye className="w-4 h-4 mr-2" />
                        <MixedText>{`${t('exams:practice.solution.show')}`}</MixedText>
                      </>
                    )}
                  </button>
                )}
              </div>
            </CardHeader>

            <CardContent>
              {!showingSolution ? (
                <div className="text-center py-8">
                  <CheckCircle className="w-12 h-12 text-surface-muted mx-auto mb-4 transition-all duration-300 hover:text-vibrant-orange" />
                </div>
              ) : currentQuestionSolution ? (
                <SelectableContent
                  onTextSelected={handleTextSelected}
                  isRTL={isRTL}
                  className="p-4 bg-muted-purple/10 rounded-lg border border-muted-purple/20 transition-all duration-300 hover:bg-muted-purple/15"
                >
                  <StudyMaterialRenderer
                    content={currentQuestionSolution.solution_text}
                    materialType="solution"
                    isRTL={isRTL}
                    fontSize={solutionFontSize}
                    onFontSizeChange={setSolutionFontSize}
                  />
                </SelectableContent>
              ) : (
                <div className="text-center py-8">
                  <LoadingSpinner className="mx-auto mb-4" />
                  <p className={`text-surface-secondary font-hebrew ${isRTL ? 'text-right' : ''}`}>
                    <MixedText>{`⏳ ${t('common:labels.loading')}...`}</MixedText>
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* PDF Export - VIP Only */}
      {isVip && (
        <div className="mt-8">
          <div className="constellation-orion p-4 rounded-xl bg-white/90 backdrop-blur-sm border border-surface-border shadow-lg">
            <div className={`flex items-center justify-center ${isRTL ? 'flex-row-reverse' : ''}`}>
              <button
                onClick={handleExportPdf}
                className={`
                  flex items-center gap-2 px-6 py-3 text-sm font-medium
                  bg-vibrant-orange/10 hover:bg-vibrant-orange/20
                  text-vibrant-orange hover:text-vibrant-orange
                  border border-vibrant-orange/30 hover:border-vibrant-orange
                  rounded-lg transition-all duration-200
                  backdrop-blur-sm
                  font-hebrew
                  ${isRTL ? 'flex-row-reverse' : ''}
                `}
              >
                <Download className="w-4 h-4" />
                <MixedText>{t('exams:pdfExport.button')}</MixedText>
              </button>
            </div>
            {pdfExportError && (
              <div className={`mt-3 text-center text-xs text-red-500 font-hebrew`}>
                <MixedText>{pdfExportError}</MixedText>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Chat Widget */}
      {practiceSession && (
        <ChatWidget
          isOpen={isChatOpen}
          onClose={() => {
            setIsChatOpen(false);
            // Clear feedback state when chat is closed
            setMessageFeedback({});
            // Clear pending message when chat is closed
            setPendingMessage(null);
          }}
          chatHistory={chatHistory}
          onChatUpdate={handleChatUpdate}
          messageFeedback={messageFeedback}
          onMessageFeedback={handleMessageFeedback}
          customChatHandler={customChatHandler}
          sessionId={practiceSession.id}
          isRTL={isRTL}
          pendingMessage={pendingMessage}
          onPendingMessageHandled={() => setPendingMessage(null)}
          mode={isVip ? 'smart' : chatMode}
          onModeChange={isVip ? undefined : handleChatModeChange}
          enableModeSwitch={!isVip}
        />
      )}

      {/* Floating Chat Toggle Button */}
      {practiceSession && (
        <button
          onClick={handleChatToggle}
          className="fixed bottom-4 md:bottom-6 right-4 md:right-6 z-[60] bg-gradient-to-r from-vibrant-orange to-vibrant-orange/90 hover:from-vibrant-orange/90 hover:to-vibrant-orange/80 text-white px-3 md:px-4 py-2.5 md:py-3 rounded-full shadow-lg shadow-vibrant-orange/30 transition-all duration-200 hover:scale-105 flex items-center gap-2 md:gap-3"
          title={t('exams:practice.askQuestion')}
        >
          <img
            src="/logo_v2.png"
            alt="StudyBel Astronaut"
            className="w-7 h-7 md:w-8 md:h-8 rounded-full bg-white/10 p-1"
          />
          <span className="font-medium text-xs md:text-sm whitespace-nowrap hidden sm:inline">
            <MixedText>{t('exams:practice.askQuestion')}</MixedText>
          </span>
        </button>
      )}
    </PageContainer>
  );
};

export default ExamPracticeView; 
